/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ChangeOptions, Change } from '@typescript-kit/core';
import { Entity, EntityData, EntityFieldKey, EntityModel } from '@typescript-kit/data';

export class LocationFieldKey extends EntityFieldKey {
  static readonly COMPANY_ID = 'companyId';
  static readonly ID = 'id';
  static readonly NAME = 'name';
  static readonly PARENT_ID = 'parentId';
  static readonly IS_CONFIRMED = 'isConfirmed';
  static readonly IS_ACTIVE = 'isActive';
}

export interface LocationData extends EntityData {
  companyId?: string;
  id?: string;
  name?: string;
  parentId?: string;
  isConfirmed?: boolean;
  isActive?: boolean;
}

export class Location extends Entity implements LocationData {
  readonly companyId: string;
  readonly id: string;
  readonly name: string;
  readonly parentId: string;
  readonly isConfirmed: boolean;
  readonly isActive: boolean;

  constructor(data: LocationData) {
    super(data);
    this.id = data.id;
    this.name = data.name;
    this.parentId = data.parentId;
    this.isConfirmed = data.isConfirmed;
    this.isActive = data.isActive;
  }
}

export class LocationModel extends EntityModel implements LocationData {
  static readonly KEYS = EntityModel.KEYS.concat(
    Object.keys(LocationFieldKey).map(key => LocationFieldKey[key])
  );

  constructor(data: LocationData) {
    super(data, LocationModel.KEYS);
  }

  get companyId(): string {
    return this.get(LocationFieldKey.COMPANY_ID);
  }

  get id(): string {
    return this.get(LocationFieldKey.ID);
  }

  set id(value: string) {
    this.set(LocationFieldKey.ID, value);
  }

  get name(): string {
    return this.get(LocationFieldKey.NAME);
  }

  set name(value: string) {
    this.set(LocationFieldKey.NAME, value);
  }

  get parentId(): string {
    return this.get(LocationFieldKey.PARENT_ID);
  }

  set parentId(value: string) {
    this.set(LocationFieldKey.PARENT_ID, value);
  }

  get isConfirmed(): boolean {
    return this.get(LocationFieldKey.IS_CONFIRMED);
  }

  set isConfirmed(value: boolean) {
    this.set(LocationFieldKey.IS_CONFIRMED, value);
  }

  get isActive(): boolean {
    return this.get(LocationFieldKey.IS_ACTIVE);
  }

  set isActive(value: boolean) {
    this.set(LocationFieldKey.IS_ACTIVE, value);
  }

  setValues(values: LocationData, options?: ChangeOptions): { [p: string]: Change<any> } {
    return super.setValues(values, options);
  }
}
